import EditorDialogs from './EditorDialogs';
import PaginationPlugin from './plugins/PaginationPlugin';
import {importWord} from './utils/wordImport';
import {insertImportedHtml} from './utils/insertHtml';
import {captureSelection,restoreSelection} from './utils/selection';
import {standaloneHtml,printPdf,exportWord,downloadBlob} from './utils/downloads';
import {getBannerDataUri} from './utils/banner';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {ListPlugin} from '@lexical/react/LexicalListPlugin';
import {TablePlugin} from '@lexical/react/LexicalTablePlugin';
import {TabIndentationPlugin} from '@lexical/react/LexicalTabIndentationPlugin';
import {HorizontalRulePlugin} from '@lexical/react/LexicalHorizontalRulePlugin';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';

import './DocEditor.css';
import {DEFAULT_FIELDS, SYSTEM_TOKENS} from './constants';
import {EDITOR_NODES} from './nodes';
import {buildOutput, buildDocument} from './utils/htmlIO';
import {validateDocument, extractTokens} from './utils/validate';
import StructurePlugin from './plugins/StructurePlugin.jsx';
import PastePlugin from './plugins/PastePlugin.jsx';
import TokenHighlightPlugin from './plugins/TokenHighlightPlugin.jsx';
import FindReplacePlugin from './plugins/FindReplacePlugin.jsx';
import ToolbarPlugin from './plugins/ToolbarPlugin.jsx';
import PreviewModal from './PreviewModal.jsx';

const THEME = {
  paragraph: 'de-p',
  heading: {h1: 'de-h1', h2: 'de-h2', h3: 'de-h3'},
  text: {bold: 'de-bold', italic: 'de-italic', underline: 'de-underline'},
  list: {ul: 'de-ul', ol: 'de-ol', listitem: 'de-li', nested: {listitem: 'de-nested-li'}},
  table: 'de-table',
  tableCell: 'de-td',
  tableCellHeader: 'de-td',
  hr: 'de-hr',
};

const normalizeFields = (fields) => {
  const list = (fields && fields.length ? fields : DEFAULT_FIELDS).map((f) =>
    typeof f === 'string' ? {key: f, label: f.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())} : f
  );
  return list.some((f) => f.key === 'room_details')
    ? list
    : [...list, {key: 'room_details', label: 'Room details'}];
};

function Shell({initialHtml, fields, onSave}) {
  const [editor] = useLexicalComposerContext();
  const fieldList = useMemo(() => normalizeFields(fields), [fields]);
  const allowed = useMemo(() => new Set([...fieldList.map((f) => f.key), ...SYSTEM_TOKENS]), [fieldList]);

  const baselineRef = useRef('');
  const scrollRef = useRef(null);
  const savedScroll = useRef(0);
  const previewSelection=useRef(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [findOpen, setFindOpen] = useState(false);
  const [previewBody, setPreviewBody] = useState(null);

  const [dialog,setDialog]=useState(null);
  const [busy,setBusy]=useState(false);
  const [notice,setNotice]=useState('');
  const importSelection=useRef(null);
  const onIssue=useCallback(message=>setNotice(message),[]);
  const closeDialog=useCallback(()=>{setDialog(null);editor.focus();},[editor]);
  const openUpload=selection=>{importSelection.current=selection||captureSelection(editor);setDialog('upload');};
  const handleImport=async(file,mode)=>{
    if(mode==='replace'&&!window.confirm('Replace all document content with this Word file?'))return;
    if(file.size>5*1024*1024&&!window.confirm('This file exceeds 5 MB. Continue?'))return;
    setBusy(true);
    try {const result=await importWord(file);insertImportedHtml(editor,result.html,mode,importSelection.current);setDirty(true);setNotice(result.messages.join(' '));setDialog(null);}
    catch(e){setErrors([e.message]);}finally{setBusy(false);}
  };
  const handleDownload=async(type,samples)=>{
    setBusy(true);
    try {
      const body=buildOutput(editor);
      if(type==='pdf')await printPdf(body,samples);
      if(type==='html')downloadBlob(new Blob([await standaloneHtml(body,samples)],{type:'text/html;charset=utf-8'}),'agreement.html');
      if(type==='docx')await exportWord(body,samples);
    } catch(e){setErrors([e.message]);}finally{setBusy(false);}
  };
  const previewing = previewBody !== null;

  const handleSave = useCallback(async () => {
    if (saving || previewing) return;
    const body = buildOutput(editor);
    const problems = validateDocument(body, allowed);
    if (problems.length) { setErrors(problems); return; }
    setErrors([]);
    setSaving(true);
    try {
      // The header banner is embedded as base64 inside the saved <style>, so the backend needs no image file or {{logo}} replacement.
      const logo = await getBannerDataUri();
      await onSave?.({html: buildDocument(body), tokens: extractTokens(body)});
      baselineRef.current = body;
      setDirty(buildOutput(editor) !== body);
    } catch (err) {
      setErrors([`Save failed: ${err?.message || err}`]);
    } finally {
      setSaving(false);
    }
  }, [editor, allowed, onSave, saving, previewing]);

  const openPreview = () => {
    previewSelection.current=captureSelection(editor);
    savedScroll.current = scrollRef.current ? scrollRef.current.scrollTop : 0;
    setFindOpen(false);
    setPreviewBody(buildOutput(editor));
  };
  const closePreview = useCallback(() => {
    setPreviewBody(null);
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = savedScroll.current;
      editor.update(()=>restoreSelection(previewSelection.current));
      editor.focus();
    });
  }, [editor]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const onKeyDown = (e) => {
    if (e.key==='F1'){e.preventDefault();setDialog('help');return;}
    if (previewing) return;
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    const k = e.key.toLowerCase();
    if (k === 's') { e.preventDefault(); handleSave(); }
    else if (k === 'f' || k === 'h') { e.preventDefault(); setFindOpen(true); }
  };

  return (
    <div className="de-root" onKeyDown={onKeyDown}>
      <ToolbarPlugin
        fields={fieldList}
        dirty={dirty}
        saving={saving}
        disabled={previewing || busy || saving}
        onSave={handleSave}
        onPreview={openPreview}
        onFind={() => setFindOpen(true)}
        onHelp={() => setDialog('help')}
        onUpload={openUpload}
        onDownload={() => setDialog('download')}
      />
      {errors.length > 0 && (
        <div className="de-errors" role="alert">
          <strong>Cannot save. Fix these first:</strong>
          <ul>{errors.map((m) => <li key={m}>{m}</li>)}</ul>
          <button type="button" onClick={() => setErrors([])}>Dismiss</button>
        </div>
      )}
      {notice && <div className="de-page-count" role="status">{notice} <button type="button" onClick={()=>setNotice('')}>Dismiss</button></div>}
      <div className="de-scroll" ref={scrollRef}>
        <RichTextPlugin
          contentEditable={<ContentEditable className="de-editable" aria-label="Document editor" />}
          placeholder={null}
          ErrorBoundary={LexicalErrorBoundary}
        />
      </div>

      <PaginationPlugin onIssue={onIssue}/>
      <HistoryPlugin />
      <ListPlugin />
      <TablePlugin hasCellMerge={false} hasCellBackgroundColor={false} />
      <TabIndentationPlugin />
      <HorizontalRulePlugin />
      <PastePlugin />
      <TokenHighlightPlugin />
      <StructurePlugin initialHtml={initialHtml} baselineRef={baselineRef} onDirtyChange={setDirty} />
      <FindReplacePlugin open={findOpen} disabled={previewing} onClose={() => setFindOpen(false)} />
      {dialog && <EditorDialogs type={dialog} onClose={closeDialog} onImport={handleImport} onDownload={handleDownload} busy={busy}/>}
      {previewing && <PreviewModal body={previewBody} onClose={closePreview} />}
    </div>
  );
}

/**
 * <DocEditor initialHtml={html} fields={[...]} onSave={(payload) => api(payload)} />
 * payload = { html: string, tokens: string[] }
 * If initialHtml is empty the default 8-page template loads.
 */
export default function DocEditor({initialHtml = '', fields = DEFAULT_FIELDS, onSave}) {
  const initialConfig = useMemo(
    () => ({
      namespace: 'DocEditor',
      nodes: EDITOR_NODES,
      theme: THEME,
      editorState: null,
      onError: (e) => { throw e; },
    }),
    []
  );
  return (
    <LexicalComposer initialConfig={initialConfig}>
      <Shell initialHtml={initialHtml} fields={fields} onSave={onSave} />
    </LexicalComposer>
  );
}
